import { FC, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { MutualFundFamily } from 'api/models/MutualFundFamily';
import { FundFamilySuggestion } from './FundFamiliesSection.types';
import { acceptSuggestion, fetchFamilies, fetchSuggestions } from './mutualFundFamilyService';

const FAMILIES_QUERY_KEY = 'mutual-fund-families';
const SUGGESTIONS_QUERY_KEY = 'mutual-fund-family-suggestions';

const FundFamiliesSection: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [expandedSuggestion, setExpandedSuggestion] = useState<string | null>(null);
  const [editingNames, setEditingNames] = useState<Record<string, string>>({});
  const [skippedSuggestions, setSkippedSuggestions] = useState<Set<string>>(new Set());

  const { data: familiesData, isLoading: familiesLoading } = useQuery({
    queryKey: [FAMILIES_QUERY_KEY],
    queryFn: fetchFamilies,
  });
  const families: MutualFundFamily[] = Array.isArray(familiesData) ? familiesData : [];

  const { data: suggestionsData, isLoading: suggestionsLoading, refetch: refetchSuggestions } = useQuery({
    queryKey: [SUGGESTIONS_QUERY_KEY],
    queryFn: fetchSuggestions,
    enabled: suggestionsOpen,
  });
  const suggestions: FundFamilySuggestion[] = Array.isArray(suggestionsData) ? suggestionsData : [];

  const acceptMutation = useMutation({
    mutationFn: acceptSuggestion,
    onSuccess: (_, variables) => {
      enqueueSnackbar(`Family "${variables.name}" created`, { variant: 'success' });
      setSkippedSuggestions(prev => new Set([...prev, variables.name]));
      queryClient.invalidateQueries({ queryKey: [FAMILIES_QUERY_KEY] });
      refetchSuggestions();
    },
    onError: () => {
      enqueueSnackbar('Failed to create family', { variant: 'error' });
    },
  });

  const handleAccept = (suggestion: FundFamilySuggestion) => {
    const name = editingNames[suggestion.suggested_name] ?? suggestion.suggested_name;
    acceptMutation.mutate({ name: name.trim(), members: suggestion.members });
  };

  const handleSkip = (suggestion: FundFamilySuggestion) => {
    setSkippedSuggestions(prev => new Set([...prev, suggestion.suggested_name]));
  };

  const visibleSuggestions = suggestions.filter(s => !skippedSuggestions.has(s.suggested_name));

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

      {/* Left: known families */}
      <Paper variant="outlined" sx={{ flex: '0 0 340px', overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600}>Known Families</Typography>
          {!familiesLoading && (
            <Chip label={families.length} size="small" color="primary" variant="outlined" />
          )}
        </Box>

        {familiesLoading && (
          <Box sx={{ p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!familiesLoading && families.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">No families defined yet.</Typography>
          </Box>
        )}

        {!familiesLoading && families.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Issuers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {families.map(f => (
                <TableRow key={f.id} hover>
                  <TableCell>{f.name}</TableCell>
                  <TableCell align="right">
                    <Chip label={f.issuer_count} size="small" variant="outlined" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Right: suggestions */}
      <Paper variant="outlined" sx={{ flex: 1, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Suggestions</Typography>
            <Typography variant="caption" color="text.secondary">
              Unmatched fund names clustered by prefix — review and accept to create new families
            </Typography>
          </Box>
          <Button
            variant={suggestionsOpen ? 'outlined' : 'contained'}
            size="small"
            onClick={() => setSuggestionsOpen(v => !v)}
            endIcon={suggestionsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ flexShrink: 0, ml: 2 }}
          >
            {suggestionsOpen ? 'Hide' : 'Load'}
          </Button>
        </Box>

        <Box sx={{ p: 2 }}>
          {!suggestionsOpen && (
            <Typography variant="body2" color="text.secondary">
              Click Load to cluster unmatched fund names and review suggestions.
            </Typography>
          )}

          {suggestionsOpen && suggestionsLoading && <CircularProgress size={20} />}

          {suggestionsOpen && !suggestionsLoading && visibleSuggestions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No suggestions — all fund names are matched or too few to cluster.
            </Typography>
          )}

          {suggestionsOpen && !suggestionsLoading && visibleSuggestions.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {visibleSuggestions.length} suggestion{visibleSuggestions.length !== 1 ? 's' : ''} remaining
              </Typography>
              {visibleSuggestions.map(suggestion => {
                const currentName = editingNames[suggestion.suggested_name] ?? suggestion.suggested_name;
                const isExpanded = expandedSuggestion === suggestion.suggested_name;

                return (
                  <Box
                    key={suggestion.suggested_name}
                    sx={{ mb: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        value={currentName}
                        onChange={e => setEditingNames(prev => ({ ...prev, [suggestion.suggested_name]: e.target.value }))}
                        sx={{ flex: 1, minWidth: 180, bgcolor: 'background.paper', borderRadius: 1 }}
                      />
                      <Chip label={`${suggestion.member_count} funds`} size="small" />
                      <Tooltip title="Show member fund names">
                        <IconButton size="small" onClick={() => setExpandedSuggestion(isExpanded ? null : suggestion.suggested_name)}>
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleAccept(suggestion)}
                        disabled={acceptMutation.isPending || !currentName.trim()}
                      >
                        Accept
                      </Button>
                      <Button size="small" color="inherit" onClick={() => handleSkip(suggestion)}>
                        Skip
                      </Button>
                    </Box>

                    <Collapse in={isExpanded}>
                      <Box sx={{ mt: 1, pl: 0.5 }}>
                        {suggestion.members.map(m => (
                          <Typography key={m} variant="caption" display="block" color="text.secondary">
                            {m}
                          </Typography>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Paper>

    </Box>
  );
};

export default FundFamiliesSection;
